import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { MaximizeIcon } from '../../Icons/DashboardIcons';
import TypesOfProjectsChart from './charts/TypesOfProjectsChart';
import YearWiseDistributionChart from './charts/YearWiseDistributionChart';
import DepartmentWiseProjectsChart from './charts/DepartmentWiseProjectsChart';
import TrainingsVsQuizzesChart from './charts/TrainingsVsQuizzesChart';

// Import reusable components
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from './common';

// Import API
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';

// Import sample data utilities
import { isDataEmpty, getSampleDataForPeriod } from '../../utilities/sampleData/tlDashboardSampleData';

const ProjectsTab = ({ selectedPeriod = 'weekly' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [maximizedChart, setMaximizedChart] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch data when period or refreshTrigger changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await TLDashboardAPIs.getProjects(selectedPeriod);
        setData(response);
      } catch (err) {
        console.error('Error fetching projects data:', err);
        setError('Failed to load projects data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, refreshTrigger]);

  const handleRetry = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          gap: 2
        }}
      >
        <CircularProgress size={48} sx={{ color: '#007bff' }} />
        <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
          Fetching projects data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={handleRetry} />;
  }

  // Extract data from API response with sample data fallback
  const totalProjects = data?.total_projects || 0;

  const rawTypesOfProjects = data?.types_of_projects || [];
  const isTypesEmpty = isDataEmpty(rawTypesOfProjects, 'array');
  const typesOfProjects = isTypesEmpty ? getSampleDataForPeriod('typesOfProjects') : rawTypesOfProjects;

  const rawYearDistribution = data?.year_distribution || [];
  const isYearDistEmpty = isDataEmpty(rawYearDistribution, 'array');
  const yearDistribution = isYearDistEmpty ? getSampleDataForPeriod('yearDistribution') : rawYearDistribution;

  const rawDepartmentProjects = data?.department_projects || [];
  const isDeptProjectsEmpty = isDataEmpty(rawDepartmentProjects, 'array');
  const departmentProjects = isDeptProjectsEmpty ? getSampleDataForPeriod('departmentProjects') : rawDepartmentProjects;

  const rawTrainingsVsQuizzes = data?.trainings_vs_quizzes || {
    trainings_completed: 0,
    quizzes_solved: 0,
    total_workshops: 0,
    completion_rate: '0%'
  };
  const isTrainingsEmpty = rawTrainingsVsQuizzes.trainings_completed === 0 && rawTrainingsVsQuizzes.quizzes_solved === 0;
  console.log("isTrainingsEmpty", isTrainingsEmpty)
  const trainingsVsQuizzes = isTrainingsEmpty ? getSampleDataForPeriod('trainingsVsQuizzes') : rawTrainingsVsQuizzes;
  console.log("trainingsVsQuizzes", trainingsVsQuizzes)

  // Helper function to get chart title
  const getChartTitle = (chartType) => {
    switch (chartType) {
      case 'typesOfProjects': return 'Types Of Projects';
      case 'yearDistribution': return 'Year-wise Distribution';
      case 'departmentProjects': return 'Department-wise Projects';
      case 'trainingsVsQuizzes': return 'Trainings Completed V/S Quizzes Solved';
      default: return '';
    }
  };

  // Helper function to render chart content in fullscreen
  const renderChartContent = (chartType) => {
    switch (chartType) {
      case 'typesOfProjects':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <TypesOfProjectsChart data={typesOfProjects} />
          </Box>
        );
      case 'yearDistribution':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <YearWiseDistributionChart data={yearDistribution} />
            </Box>
          </Box>
        );
      case 'departmentProjects':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DepartmentWiseProjectsChart data={departmentProjects} />
            </Box>
          </Box>
        );
      case 'trainingsVsQuizzes':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <TrainingsVsQuizzesChart data={trainingsVsQuizzes} />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Fullscreen Chart Modal */}
      <FullscreenChartModal
        open={Boolean(maximizedChart)}
        onClose={() => setMaximizedChart(null)}
        title={getChartTitle(maximizedChart)}
      >
        {renderChartContent(maximizedChart)}
      </FullscreenChartModal>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: '16px', md: '24px' },
        }}
      >
        {/* Types Of Projects */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '360px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                  color: '#333333',
                }}
              >
                Types Of Projects
              </Typography>
              <SampleDataBadge show={isTypesEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('typesOfProjects')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#475890',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#fafafa',
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                bgcolor: '#ffffff',
                borderRadius: { xs: '10px', md: '12px' },
                border: '1px solid #e6e6e6',
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: '20px', md: '30px' },
              }}
            >
              <TypesOfProjectsChart data={typesOfProjects} />
            </Box>
          </Box>
        </Box>

        {/* Year-wise Distribution */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '360px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                  color: '#333333',
                }}
              >
                Year-wise Distribution
              </Typography>
              <SampleDataBadge show={isYearDistEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('yearDistribution')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#12ab0a',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#fafafa',
              padding: '12px',
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                bgcolor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e6e6e6',
                flex: 1,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minHeight: 0,
              }}
            >
              <YearWiseDistributionChart data={yearDistribution} />
            </Box>
          </Box>
        </Box>

        {/* Department-wise Projects */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            height: { xs: '380px', sm: '460px', md: '550px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '12px 16px', md: '16px 20px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '15px', md: '18px' },
                  fontWeight: 500,
                  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                  color: '#333333',
                }}
              >
                Department-wise Projects
              </Typography>
              <SampleDataBadge show={isDeptProjectsEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('departmentProjects')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#f99f24',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#fafafa',
              padding: '12px',
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                bgcolor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e6e6e6',
                flex: 1,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minHeight: 0,
              }}
            >
              <DepartmentWiseProjectsChart data={departmentProjects} />
            </Box>
          </Box>
        </Box>

        {/* Trainings Completed V/S Quizzes Solved */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            height: { xs: '380px', sm: '460px', md: '550px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '12px 16px', md: '16px 20px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '15px', md: '18px' },
                  fontWeight: 500,
                  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                  color: '#333333',
                }}
              >
                Trainings Completed V/S Quizzes Solved
              </Typography>
              <SampleDataBadge show={isTrainingsEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('trainingsVsQuizzes')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#c144ff',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#ffffff',
              padding: { xs: '12px', md: '16px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TrainingsVsQuizzesChart data={trainingsVsQuizzes} />
          </Box>
        </Box>
      </Box>
    </Box >
  );
};

export default ProjectsTab;
